// Storage Service - Handle file uploads to Supabase Storage

import { Injectable } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { getSupabaseClient } from '../supabase/supabase.client';

export interface UploadResult {
  path: string;
  publicUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private supabase = getSupabaseClient();

  /**
   * Upload a file to a storage bucket
   */
  uploadFile(
    bucket: string,
    path: string,
    file: File,
    options?: { contentType?: string; cacheControl?: string; upsert?: boolean }
  ): Observable<UploadResult> {
    const contentType = options?.contentType || file.type || 'image/jpeg';
    return from(
      this.supabase.storage
        .from(bucket)
        .upload(path, file, {
          contentType,
          cacheControl: options?.cacheControl || '3600',
          // Paths are timestamped; upsert:false avoids SELECT checks removed from public buckets.
          upsert: options?.upsert ?? false,
        })
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          throw new Error(`Upload failed: ${error.message}`);
        }

        if (!data) {
          throw new Error('Upload returned no data');
        }

        // Check if bucket is public
        const publicBuckets = ['avatars', 'request-photos', 'message-photos'];
        const isPublic = publicBuckets.includes(bucket);

        const result: UploadResult = {
          path: data.path,
        };

        if (isPublic) {
          const { data: urlData } = this.supabase.storage
            .from(bucket)
            .getPublicUrl(data.path);
          result.publicUrl = urlData.publicUrl;
        }

        return result;
      }),
      catchError((error) => {
        console.error('Storage upload error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get public URL for a file (for public buckets)
   */
  getPublicUrl(bucket: string, path: string): string {
    const { data } = this.supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  }

  /**
   * Get signed URL for a file (for private buckets)
   */
  getSignedUrl(
    bucket: string,
    path: string,
    expiresIn: number = 3600
  ): Observable<string> {
    return from(
      this.supabase.storage.from(bucket).createSignedUrl(path, expiresIn)
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          throw new Error(`Failed to get signed URL: ${error.message}`);
        }
        return data.signedUrl;
      }),
      catchError((error) => {
        console.error('Get signed URL error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a file from storage
   */
  deleteFile(bucket: string, path: string): Observable<void> {
    return from(
      this.supabase.storage.from(bucket).remove([path])
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          throw new Error(`Delete failed: ${error.message}`);
        }
        return undefined;
      }),
      catchError((error) => {
        console.error('Storage delete error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Upload avatar and return public URL
   */
  uploadAvatar(userId: string, file: File): Observable<string> {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop() || 'jpg';
    // Use a versioned filename so public URL changes and clients don't reuse a cached avatar.
    const fileName = `${userId}/avatar_${timestamp}.${fileExt}`;
    const bucket = 'avatars';

    return this.uploadFile(bucket, fileName, file, {
      contentType: file.type,
      cacheControl: '3600',
    }).pipe(
      map((result) => {
        if (!result.publicUrl) {
          throw new Error('Failed to get public URL for avatar');
        }
        return result.publicUrl;
      })
    );
  }

  /**
   * Upload cleaner document
   */
  uploadCleanerDocument(
    userId: string,
    type: string,
    file: File
  ): Observable<UploadResult> {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${type}_${timestamp}.${fileExt}`;
    const bucket = 'documents';

    return this.uploadFile(bucket, fileName, file, {
      contentType: file.type,
    });
  }

  /**
   * Upload job photo
   */
  uploadJobPhoto(
    jobId: string,
    file: File,
    type: 'before' | 'after' | 'evidence'
  ): Observable<UploadResult> {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop();
    const fileName = `${jobId}/${type}_${timestamp}.${fileExt}`;
    const bucket = 'job-photos';

    return this.uploadFile(bucket, fileName, file, {
      contentType: file.type,
    });
  }

  /**
   * Upload quote request photo (client). Path: {clientId}/{timestamp}.{ext}
   * Returns public URL for storing in quote_requests.photos.
   */
  uploadRequestPhoto(clientId: string, file: File): Observable<string> {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop() || 'jpg';
    const fileName = `${clientId}/${timestamp}.${fileExt}`;
    const bucket = 'request-photos';

    return this.uploadFile(bucket, fileName, file, {
      contentType: file.type,
    }).pipe(
      map((result) => {
        if (!result.publicUrl) {
          const { data } = this.supabase.storage.from(bucket).getPublicUrl(result.path);
          return data.publicUrl;
        }
        return result.publicUrl;
      })
    );
  }

  /**
   * Upload chat message photo. Path: {userId}/{timestamp}.{ext}
   * Returns public URL for storing in messages.attachments.
   */
  uploadMessagePhoto(userId: string, file: File): Observable<string> {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop() || 'jpg';
    const fileName = `${userId}/${timestamp}.${fileExt}`;
    const bucket = 'message-photos';

    return this.uploadFile(bucket, fileName, file, {
      contentType: file.type,
    }).pipe(
      map((result) => {
        if (!result.publicUrl) {
          const { data } = this.supabase.storage.from(bucket).getPublicUrl(result.path);
          return data.publicUrl;
        }
        return result.publicUrl;
      })
    );
  }

  /**
   * Extract storage object path from a public Supabase storage URL for a known bucket.
   */
  pathFromPublicUrl(bucket: string, publicUrl: string): string | null {
    const marker = `/storage/v1/object/public/${bucket}/`;
    const idx = publicUrl.indexOf(marker);
    if (idx < 0) return null;
    return decodeURIComponent(publicUrl.slice(idx + marker.length));
  }

  /**
   * Upload dispute evidence
   */
  uploadDisputeEvidence(
    disputeId: string,
    file: File
  ): Observable<UploadResult> {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop();
    const fileName = `${disputeId}/evidence_${timestamp}.${fileExt}`;
    const bucket = 'dispute-evidence';

    return this.uploadFile(bucket, fileName, file, {
      contentType: file.type,
    });
  }

  /**
   * List files in a folder
   */
  listFiles(bucket: string, folder: string): Observable<string[]> {
    return from(
      this.supabase.storage.from(bucket).list(folder)
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          throw new Error(`List files failed: ${error.message}`);
        }
        return data?.map((file) => file.name) || [];
      }),
      catchError((error) => {
        console.error('List files error:', error);
        return throwError(() => error);
      })
    );
  }
}
